import { FC } from 'react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { NoteStatus, WritebackStatus } from '@weave/schema-gen-ts/dist/schemas/sms/writeback/v1/writeback_service.pb';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { useDebouncedValue } from '@frontend/timer';
import { theme } from '@frontend/theme';
import { DataSyncIcon, FontColorType, IconProps, useTooltip, Text } from '@frontend/design-system';

const MotionText = motion.create(Text);

type TextWritebackStatusProps = {
  showLabel: boolean;
  noteStatus: NoteStatus;
  direction: Direction;
};

const statusMap: Record<
  WritebackStatus,
  { color: FontColorType; icon: FC<React.PropsWithChildren<IconProps>>; isError: boolean }
> = {
  [WritebackStatus.WRITEBACK_STATUS_SUCCESS]: {
    color: 'success',
    icon: DataSyncIcon,
    isError: false,
  },
  [WritebackStatus.WRITEBACK_STATUS_QUEUED]: {
    color: 'success',
    icon: DataSyncIcon,
    isError: false,
  },
  [WritebackStatus.WRITEBACK_STATUS_FAILURE]: {
    color: 'error',
    icon: DataSyncIcon,
    isError: true,
  },
  [WritebackStatus.WRITEBACK_STATUS_UNSPECIFIED]: {
    color: 'error',
    icon: DataSyncIcon,
    isError: true,
  },
};

export const TextWritebackStatus = ({ showLabel, noteStatus, direction }: TextWritebackStatusProps) => {
  const { t } = useTranslation('inbox');
  const { color, icon: Icon, isError } = statusMap[noteStatus.status];
  const label = isError
    ? noteStatus.errorMsg
    : showLabel
    ? t('Successfully sent to {{pmsName}}', { pmsName: noteStatus.pmsName })
    : t('Sent to {{pmsName}}', { pmsName: noteStatus.pmsName });
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });
  const { debouncedValue: debouncedShowLabel } = useDebouncedValue(showLabel, 300);

  return (
    <motion.span
      css={{
        minHeight: 18, // Avoid shifting when label appears/disappears
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        flexDirection: direction === Direction.DIRECTION_INBOUND ? 'row-reverse' : 'row',
      }}
    >
      <AnimatePresence>
        {showLabel && (
          <MotionText
            color={color}
            size='small'
            initial={debouncedShowLabel ? { width: 'auto', opacity: 1 } : { width: 0, opacity: 0 }}
            animate={{ width: 'auto', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            css={{
              textWrap: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {label}
          </MotionText>
        )}
      </AnimatePresence>
      <span {...triggerProps}>
        <Icon color={color} size={14} />
      </span>
      {!showLabel && <Tooltip {...tooltipProps}>{label}</Tooltip>}
    </motion.span>
  );
};
